import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { EntityData } from "@/services/api/types";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { multiSelectOption } from "@/components/controls/MultiSelect";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import { format } from "date-fns";
import { toActivityFormValues } from "@/utils/datetime";
import { AddButtonFooter } from "@/components/ui/add-button-footer";

interface UseActivityModalFieldsParams {
  activityData?: any;
  isEditMode: boolean;
  entityType: EntityType;
  entityData: EntityData | undefined;
  participantsOptions: multiSelectOption[];
  onParticipantsSearch: (search: string) => void;
  isLoadingParticipants: boolean;
  setSelectedParticipants: (participants: string[]) => void;
  selectedParticipants: string[];
  formSetValue: any;
}

interface UseActivityModalFieldsReturn {
  getBaseFields: () => Record<string, FormFieldNewType>;
  getDateField: () => FormFieldNewType;
  getTimeField: () => FormFieldNewType;
  getParticipantsField: (label?: string) => FormFieldNewType;
  getSubjectField: () => FormFieldNewType;
  processFieldsForEditMode: (fields: FormFieldNewType[]) => FormFieldNewType[];
  isCreatePersonModalOpen: boolean;
  setIsCreatePersonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onPersonCreation: (personId: string, personName: string) => void;
}

export const useActivityModalFields = ({
  activityData,
  isEditMode,
  entityType,
  entityData,
  participantsOptions,
  onParticipantsSearch,
  isLoadingParticipants,
  setSelectedParticipants,
  selectedParticipants,
  formSetValue
}: UseActivityModalFieldsParams): UseActivityModalFieldsReturn => {
  const { t } = useTranslation();

  const [isCreatePersonModalOpen, setIsCreatePersonModalOpen] = useState(false);

  const onPersonCreation = (personId: string, personName: string) => {
    if (personId && personName) {
      if (!selectedParticipants.includes(personId)) {
        const newSelectedParticipants = [...selectedParticipants, personId];
        setSelectedParticipants(newSelectedParticipants);
        if (typeof formSetValue === "function") {
          formSetValue("participants", newSelectedParticipants, {
            shouldValidate: true
          });
        }
      }
      setIsCreatePersonModalOpen(false);
    }
  };

  const getDateField = useMemo(() => {
    return (): FormFieldNewType => ({
      name: "date",
      label: t("activities.activityModal.date"),
      type: "date",
      required: true,
      labelPosition: "left",
      fieldActionProps: {
        placeholder: t("activities.activityModal.selectDate")
      }
    });
  }, [t]);

  const getTimeField = useMemo(() => {
    return (): FormFieldNewType => ({
      name: "time",
      label: t("activities.activityModal.time"),
      type: "time",
      required: false,
      labelPosition: "left",
      fieldActionProps: {
        placeholder: t("activities.activityModal.selectTime")
      }
    });
  }, [t]);

  const getParticipantsField = useMemo(() => {
    return (label?: string): FormFieldNewType => ({
      name: "participants",
      label: label || t("activities.activityModal.participants"),
      type: "multiSelect",
      required: true,
      labelPosition: "left",
      fieldActionProps: {
        placeholder: t("activities.activityModal.addParticipants"),
        options: participantsOptions || [],
        showSearch: true,
        onSearch: onParticipantsSearch,
        isLoading: isLoadingParticipants,
        entity: EntityType.PERSON,
        modalPopover: true,
        onValueChange: (values: string[]) => {
          setSelectedParticipants(values);
        },
        popOverFooter: (
          <AddButtonFooter
            buttonText={t("common.addNewPerson")}
            onClick={() => setIsCreatePersonModalOpen(true)}
          />
        )
      },
      defaultValue:
        entityType === EntityType.PERSON &&
        participantsOptions &&
        participantsOptions.find(
          (participant: multiSelectOption) =>
            participant.value === entityData?.id
        )
          ? [entityData?.id || ""]
          : undefined
    });
  }, [
    t,
    participantsOptions,
    onParticipantsSearch,
    isLoadingParticipants,
    setSelectedParticipants,
    entityType,
    entityData?.id
  ]);

  const getSubjectField = useMemo(() => {
    return (): FormFieldNewType => ({
      name: "subject",
      label: t("activities.activityModal.subject"),
      type: "text",
      required: true,
      labelPosition: "left",
      fieldActionProps: {
        placeholder: t("activities.activityModal.addSubject")
      }
    });
  }, [t]);

  const getBaseFields = useMemo(() => {
    return (): Record<string, FormFieldNewType> => ({
      date: getDateField(),
      time: getTimeField(),
      subject: getSubjectField(),
      participants: getParticipantsField()
    });
  }, [getDateField, getTimeField, getSubjectField, getParticipantsField]);

  const processFieldsForEditMode = useMemo(() => {
    return (fields: FormFieldNewType[]): FormFieldNewType[] => {
      if (!isEditMode || !activityData) {
        return fields;
      }

      return fields.map((field) => {
        let updatedField = { ...field };

        const isAttachmentArray = (
          value: any
        ): value is SavedAttachmentInfo[] => {
          return (
            Array.isArray(value) && value.length > 0 && "fileId" in value[0]
          );
        };

        const notes = activityData.notes;

        switch (field.name) {
          case "participants":
            updatedField.defaultValue = activityData.peopleIds || [];
            break;
          case "summary":
            if (
              notes &&
              notes.summary &&
              typeof notes.summary === "object" &&
              "summary" in notes.summary
            ) {
              updatedField.defaultValue = notes.summary.summary;
            }
            break;
          case "time":
            // The time lives inside notes.date; date-only activities have none
            {
              const time = notes?.date
                ? toActivityFormValues(notes.date)?.time
                : undefined;
              if (time) {
                updatedField.defaultValue = time;
              }
            }
            break;
          default:
            // For all other fields, check if they exist in notes
            if (notes && field.name in notes) {
              const value = notes[field.name as keyof typeof notes];

              // Skip attachment type fields
              if (isAttachmentArray(value)) {
                break;
              }

              if (typeof value === "string") {
                if (field.type === "time") {
                  try {
                    const date = new Date(value);
                    updatedField.defaultValue = format(date, "h:mm a");
                  } catch {
                    updatedField.defaultValue = "";
                  }
                } else if (field.type === "date") {
                  updatedField.defaultValue =
                    toActivityFormValues(value)?.date ?? "";
                } else {
                  updatedField.defaultValue = value;
                }
              } else if (Array.isArray(value)) {
                if (value.every((item) => typeof item === "string")) {
                  updatedField.defaultValue = value as string[];
                } else if (
                  value.every(
                    (item) => typeof item === "object" && "email" in item
                  )
                ) {
                  // Extract just the emails from EmailNameType[]
                  updatedField.defaultValue = value.map((item) => item.name);
                }
              }
            }
        }

        return updatedField;
      });
    };
  }, [isEditMode, activityData, entityType]);

  return {
    getBaseFields,
    getDateField,
    getTimeField,
    getParticipantsField,
    getSubjectField,
    processFieldsForEditMode,
    isCreatePersonModalOpen,
    setIsCreatePersonModalOpen,
    onPersonCreation
  };
};
